import { useState, useEffect } from 'react';
import PageHeader from '@/components/base/PageHeader';

export default function Kontakt() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = 'Kontakt | Druckerei Rahmede';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9likngbdkpk99j7g10g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* not JSON */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Ein Fehler ist aufgetreten.';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('success');
          return;
        }
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('Übermittlung fehlgeschlagen. Bitte versuchen Sie es später erneut.');
      setFormStatus('error');
    }
  };

  return (
    <>
      <PageHeader
        label="Kontakt"
        title="Lassen Sie uns sprechen"
        subtitle="Sie haben ein Projekt im Kopf? Wir beraten Sie gerne persönlich und erstellen ein unverbindliches Angebot."
      />

      <section className="w-full px-4 md:px-6 py-10 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-14">
            <div className="w-full lg:w-2/5">
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 md:gap-6">
                <div className="flex gap-3 md:gap-4 col-span-2 lg:col-span-1">
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-100/60 flex-shrink-0">
                    <i className="ri-map-pin-line text-base md:text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1">Adresse</h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                      Rahmede GmbH<br />
                      Unterm Eichholz 8<br />
                      58579 Schalksmühle-Heedfeld
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-100/60 flex-shrink-0">
                    <i className="ri-phone-line text-base md:text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1">Telefon</h3>
                    <a href="tel:+4923514327721" className="text-xs md:text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                      02351 4327721
                    </a>
                  </div>
                </div>
                <div className="flex gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-100/60 flex-shrink-0">
                    <i className="ri-mail-line text-base md:text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1">E-Mail</h3>
                    <a href="mailto:info@rahmede.org" className="text-xs md:text-sm text-foreground-500 hover:text-primary-600 transition-colors break-all">
                      info@rahmede.org
                    </a>
                  </div>
                </div>
                <div className="flex gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-100/60 flex-shrink-0">
                    <i className="ri-time-line text-base md:text-lg text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-1">Öffnungszeiten</h3>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                      Mo–Do: 8:00–17:00 Uhr<br />
                      Fr: 8:00–15:00 Uhr
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-3/5">
              <form
                id="kontaktformular"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-100 rounded-lg p-5 md:p-8 border border-background-200/50"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Name <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="Ihr vollständiger Name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      E-Mail <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="ihre@email.de"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="telefon" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="+49 123 456789"
                    />
                  </div>
                  <div>
                    <label htmlFor="betreff" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Betreff <span className="text-primary-500">*</span>
                    </label>
                    <select
                      id="betreff"
                      name="betreff"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Angebot">Angebot anfordern</option>
                      <option value="Siebdruck">Siebdruck</option>
                      <option value="Geschaeftsausstattung">Geschäftsausstattung</option>
                      <option value="Flyer">Flyer & Broschüren</option>
                      <option value="Plakate">Plakate & Werbemittel</option>
                      <option value="Aufkleber">Aufkleber & Etiketten</option>
                      <option value="Sonstiges">Sonstige Anfrage</option>
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="nachricht" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Ihre Nachricht <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="nachricht"
                    name="nachricht"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                    placeholder="Beschreiben Sie Ihr Projekt oder Ihre Anfrage..."
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">Maximal 500 Zeichen</p>
                </div>

                <div className="contact-honeypot">
                  <label htmlFor="phone_alt">Bitte nicht ausfüllen</label>
                  <input type="text" id="phone_alt" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                {formStatus === 'success' && (
                  <div className="mb-4 p-3 rounded-md bg-accent-100/60 text-accent-800 text-sm">
                    Vielen Dank für Ihre Nachricht! Wir melden uns schnellstmöglich bei Ihnen.
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mb-4 p-3 rounded-md bg-primary-100/60 text-primary-800 text-sm">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-md hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'loading' ? 'Wird gesendet...' : 'Nachricht senden'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="w-full">
        <iframe
          src="https://maps.google.com/maps?q=Unterm+Eichholz+8,+58579+Schalksm%C3%BChle-Heedfeld&t=h&z=17&ie=UTF8&iwloc=&output=embed"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Standort Druckerei Rahmede — Unterm Eichholz 8, 58579 Schalksmühle-Heedfeld"
          className="w-full h-[300px] md:h-[450px]"
        ></iframe>
      </section>
    </>
  );
}